import React, { createContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { getEnv } from '../config';

export const AuthContext = createContext();

const API_URL = getEnv('REACT_APP_API_URL') || '';

export function AuthProvider({ children }) {
    const [user, setUser] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || null;
        } catch {
            return null;
        }
    });
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(false);
    const [initializing, setInitializing] = useState(!!localStorage.getItem('token'));
    const [error, setError] = useState(null);

    // Configure axios with token
    useEffect(() => {
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
            delete axios.defaults.headers.common['Authorization'];
        }
    }, [token]);

    // Global 401 interceptor — auto-logout on expired token
    useEffect(() => {
        const id = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    setToken(null);
                    setUser(null);
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    delete axios.defaults.headers.common['Authorization'];
                }
                return Promise.reject(error);
            }
        );
        return () => axios.interceptors.response.eject(id);
    }, []);

    const loginWithOAuth = useCallback(async (idToken, provider, { code, redirectURI } = {}) => {
        setLoading(true);
        setError(null);
        try {
            const payload = { provider };
            if (code) {
                payload.code = code;
                payload.redirect_uri = redirectURI;
            }
            if (idToken) {
                payload.id_token = idToken;
            }
            const response = await axios.post(`${API_URL}/auth/oauth`, payload);
            const { token, leader } = response.data;

            setToken(token);
            setUser(leader);
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(leader));

            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            return true;
        } catch (err) {
            const message = err.response?.data?.message || 'Login failed';
            setError(message);
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    const acceptInvite = useCallback(async (idToken, provider, inviteToken, { code, redirectURI } = {}) => {
        setLoading(true);
        setError(null);
        try {
            const payload = { provider, invite_token: inviteToken };
            if (code) {
                payload.code = code;
                payload.redirect_uri = redirectURI;
            }
            if (idToken) {
                payload.id_token = idToken;
            }
            const response = await axios.post(`${API_URL}/auth/accept-invite`, payload);
            const { token, leader } = response.data;

            setToken(token);
            setUser(leader);
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(leader));

            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            return true;
        } catch (err) {
            const message = err.response?.data?.message || 'Failed to accept invite';
            setError(message);
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(() => {
        setToken(null);
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
    }, []);

    const fetchProfile = useCallback(async () => {
        if (!token) return;

        try {
            const response = await axios.get(`${API_URL}/api/profile`);
            setUser(response.data.data);
            localStorage.setItem('user', JSON.stringify(response.data.data));
        } catch (err) {
            console.error('Failed to fetch profile:', err);
            if (err.response?.status === 401) {
                logout();
            }
        }
    }, [token, logout]);

    // Fetch profile on mount when a token exists
    useEffect(() => {
        if (!token) {
            setInitializing(false);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const response = await axios.get(`${API_URL}/api/profile`);
                if (!cancelled) {
                    setUser(response.data.data);
                    localStorage.setItem('user', JSON.stringify(response.data.data));
                }
            } catch (err) {
                console.error('Failed to fetch profile:', err);
                if (!cancelled && err.response?.status === 401) {
                    logout();
                }
            } finally {
                if (!cancelled) {
                    setInitializing(false);
                }
            }
        })();

        return () => { cancelled = true; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const roles = user?.roles || (user?.role ? [user.role] : []);
    const isAdmin = roles.includes('admin') || roles.includes('pastor');
    const isIntake = roles.includes('intake');
    const isAuthenticated = !!token;

    return (
        <AuthContext.Provider
            value={{
                user,
                token,
                isAuthenticated,
                isAdmin,
                isIntake,
                loading,
                initializing,
                error,
                loginWithOAuth,
                acceptInvite,
                logout,
                fetchProfile,
                setError,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}
